import {
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  model,
  OnDestroy,
  OnInit,
  output,
  Signal,
  signal,
  Type,
  viewChild,
  viewChildren,
} from '@angular/core';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { DashboardWidgetComponent } from '../dashboard-widget/dashboard-widget.component';
import { CommonModule } from '@angular/common';
import { AbbButtonComponent } from '@components/abb-button/abb-button.component';
import {
  GetDashboardsQuery,
  UiSettings,
  UpdateDashboardRefreshRateMutation,
  UpdateDashboardRefreshRateMutationVariables,
  UpdateWidgetsPositionMutation,
  UpdateWidgetsPositionMutationVariables,
} from 'gql/graphql';
import {
  COLS,
  EXTRA_ROWS,
  WidgetDisplayOption,
  WidgetDisplayOptionsByType,
  WidgetType,
} from '@constants/widgets';
import { CustomWidget } from '../widgets/widget';
import { Apollo } from 'apollo-angular';
import {
  savePdfReport,
  updateDashboardRefreshRateMutation,
  updateWidgetsPositionMutation,
} from '@graphql/dashboard';
import { LOADING } from '@utils/loader';
import { ColumnChartWidgetComponent } from '../widgets/column-chart-widget/column-chart-widget.component';
import { DonutChartWidgetComponent } from '../widgets/donut-chart-widget/donut-chart-widget.component';
import { LineChartWidgetComponent } from '../widgets/line-chart-widget/line-chart-widget.component';
import { GaugeWidgetComponent } from '../widgets/gauge-widget/gauge-widget.component';
import { TableWidgetComponent } from '../widgets/table-widget/table-widget.component';
import { MinMaxWidgetComponent } from '../widgets/min-max-widget/min-max-widget.component';
import { WaterGasWidgetComponent } from '../widgets/water-gas-widget/water-gas-widget.component';
import { ContractsAndTariffsWidgetComponent } from '../widgets/contracts-and-tariffs-widget/contracts-and-tariffs-widget.component';
import { NumberAndListWidgetComponent } from '../widgets/number-and-list-widget/number-and-list-widget.component';
import { ListWithTargetWidgetComponent } from '../widgets/list-with-target-widget/list-with-target-widget.component';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { RichTextWidgetComponent } from '../widgets/rich-text-widget/rich-text-widget.component';
import { MapWidgetComponent } from '../widgets/map-widget/map-widget.component';
import { KpiWidgetComponent } from '../widgets/kpi-widget/kpi-widget.component';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@services/ui/toast.service';
import { MapService } from '@services/ui/maps.service';
import { typedObjectEntries, typedObjectFromEntries } from '@utils/objects';
import { t } from '@utils/strings';
import { PermissionsService } from '@services/user/permissions.service';
import { PAGES } from '@constants/constants';
import { UserSettingsService } from '@services/user/user-settings.service';
import { firstValueFrom } from 'rxjs';
import { CognitoService } from '@services/cognito/cognito.service';
import { listenForEventsWhen } from '@utils/angular-ext';

@Component({
  selector: 'abb-dashboard-edit',
  standalone: true,
  imports: [
    DragDropModule,
    DashboardWidgetComponent,
    CommonModule,
    AbbButtonComponent,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    TPipe,
  ],
  template: `
    <div class="bg-abb-background flex flex-col flex-1 p-2 rounded-sm">
      @if (!editMode()) {
      <div
        class="sticky top-0 z-[9999] bg-abb-background flex justify-between items-start px-2 py-2"
        [class.shadow-md]="isScrolled()"
      >
        <mat-form-field>
          <mat-label>{{ 'Refresh rate' | t }}</mat-label>
          <mat-select
            [(ngModel)]="refreshRate"
            name="refresh-rate"
            (selectionChange)="updateRefreshRate()"
            [panelClass]="'panel'"
            [disabled]="!canEdit()"
          >
            @for (refreshRateOption of refreshRates; track
            refreshRateOption.value) {
            <mat-option [value]="refreshRateOption.value">
              {{
                refreshRateOption.name
                  | t : { minutes: refreshRateOption.value }
              }}
            </mat-option>
            }
          </mat-select>
        </mat-form-field>
        <div class="flex flex-row justify-end items-center gap-16">
          <abb-button
            id="export"
            (onclick)="onExport()"
            [loading]="exporting()"
            [disabled]="renderingMaps() || exporting()"
            class="w-32"
            >{{ 'Export' | t }}</abb-button
          >
          @if(canEdit()) {
          <abb-button
            variant="clean"
            type="button"
            (onclick)="editModeChange.emit(!editMode())"
            [icon]="'mat-outlined/edit'"
          />
          }
        </div>
      </div>
      }

      <div
        class="boundary relative"
        #container
        id="dashboard-content"
        [ngStyle]="{
          height: rows() + 'vh',
          width: '100%',
          backgroundImage: editMode() ? svg() : 'none',
          backgroundSize: cellWidth() + 'px ' + cellHeight() + 'px'
        }"
      >
        @for (widget of tmpWidgets(); track widget.id) {
        <abb-dashboard-widget
          [name]="widget.title"
          [cellHeight]="cellHeight()"
          [cellWidth]="cellWidth()"
          [rowGap]="0"
          [colGap]="0"
          (dragStart)="setDraggingItem(widget.id)"
          (dragEnd)="setDraggingItem(null)"
          [dragInProgress]="draggingItem() !== null"
          [editMode]="editMode()"
          [dashboardId]="dashboardId()"
          [widget]="widget"
          [widgets]="tmpWidgets()"
          (sizeChange)="resizing(widget.id, $event)"
          (onDeleteWidget)="deleteWidget(widget.id)"
        >
          @if (componentMap[widget.type][widget.display]; as component) {
          <ng-container
            *ngComponentOutlet="
              component;
              inputs: {
                dashboardId: dashboardId(),
                widget,
                refreshRateMinutes: dashboard().refreshRate
              }
            "
          ></ng-container>
          }
        </abb-dashboard-widget>

        }
      </div>
    </div>
  `,
  styles: ``,
  host: { class: 'flex-1 flex flex-col' },
})
export class DashboardEditComponent implements OnInit, OnDestroy {
  isScrolled = signal(false);
  container = viewChild<ElementRef<HTMLDivElement>>('container');
  widgetRefs = viewChildren(DashboardWidgetComponent);
  cellWidth = signal(0);
  cellHeight = signal(0);
  editMode = input(false);
  dashboardId = computed(() => this.dashboard().id);
  refreshRate = model<number>();
  dashboard = input.required<GetDashboardsQuery['getDashboards'][number]>();
  onEndUpdateWidget = output<GetDashboardsQuery['getDashboards'][number]>();
  uiSettings: Signal<UiSettings | null>;
  canEdit = computed(() =>
    this.permissionsService.canEditPage(PAGES.DASHBOARDS)
  );
  svg = computed(() => {
    return (
      'url(data:image/svg+xml;base64,' +
      btoa(`<svg fill="#eaeaea" viewBox="0 0 ${this.cellWidth()} ${this.cellHeight()}" xmlns="http://www.w3.org/2000/svg">
  <circle cx="${this.cellWidth() / 2}" cy="${this.cellHeight() / 2}" r="${
        Math.min(this.cellWidth(), this.cellHeight()) / 5
      }" />
</svg>`) +
      ')'
    );
  });

  private prefix = inject(TRANSLATION_PREFIX);
  loading = inject(LOADING);

  refreshRates = [
    { value: 10, name: 'Minutes {{minutes}}' },
    { value: 15, name: 'Minutes {{minutes}}' },
    { value: 30, name: 'Minutes {{minutes}}' },
    { value: 60, name: 'Minutes {{minutes}}' },
  ];

  static readonly COMPONENT_MAP_BY_DISPLAY: Record<
    WidgetDisplayOption,
    Type<CustomWidget>
  > = {
    [WidgetDisplayOption.CHART_COLUMN]: ColumnChartWidgetComponent,
    [WidgetDisplayOption.CHART_DONUT]: DonutChartWidgetComponent,
    [WidgetDisplayOption.CHART_LINEAR]: LineChartWidgetComponent,
    [WidgetDisplayOption.GAUGE]: GaugeWidgetComponent,
    [WidgetDisplayOption.TABLE]: TableWidgetComponent,
    [WidgetDisplayOption.MIN_MAX]: MinMaxWidgetComponent,
    [WidgetDisplayOption.WATER_GAS]: WaterGasWidgetComponent,
    [WidgetDisplayOption.CONTRACTS_TARIFFS]: ContractsAndTariffsWidgetComponent,
    [WidgetDisplayOption.NUMBER_AND_LIST]: NumberAndListWidgetComponent,
    [WidgetDisplayOption.LIST_WITH_TARGET]: ListWithTargetWidgetComponent,
    [WidgetDisplayOption.RICH_TEXT]: RichTextWidgetComponent,
    [WidgetDisplayOption.MAP]: MapWidgetComponent,
    [WidgetDisplayOption.KPI]: KpiWidgetComponent,
  };

  static readonly COMPONENT_MAP: Record<
    WidgetType,
    Record<WidgetDisplayOption, Type<CustomWidget>>
  > = typedObjectFromEntries(
    typedObjectEntries(WidgetDisplayOptionsByType).map(
      ([type, displayOptions]) => [
        type,
        typedObjectFromEntries(
          displayOptions.map(({ id }) => [
            id,
            DashboardEditComponent.COMPONENT_MAP_BY_DISPLAY[id],
          ])
        ),
      ]
    )
  );

  readonly componentMap = DashboardEditComponent.COMPONENT_MAP as Record<
    string,
    Record<string, Type<CustomWidget>>
  >;

  tmpWidgets = signal<GetDashboardsQuery['getDashboards'][number]['widgets']>(
    []
  );
  editModeChange = output<boolean>();

  visibleRows = computed(() => {
    const widgets = this.tmpWidgets();
    if (widgets.length === 0) {
      return 1;
    }
    return widgets.reduce((maxRow, w) => {
      const candidate = w.row + w.rowSpan - 1;
      if (candidate > maxRow) {
        return candidate;
      }
      return maxRow;
    }, 1);
  });
  rows = computed(() =>
    this.editMode() ? this.visibleRows() + EXTRA_ROWS : this.visibleRows()
  );
  draggingItem = signal<string | null>(null);
  private previousWidgetCount = 0;
  private isUpdatingPosition = false;
  private previousWidgetIds = new Set<string>();
  private currentDashboardId: string | null = null;

  updateCellSize = () => {
    const container = this.container()?.nativeElement;
    if (!container) return;


    this.cellWidth.set(container.offsetWidth / COLS);


    const hasWidgets = this.tmpWidgets().length > 0;
    const containerHeight = hasWidgets ? container.scrollHeight : container.offsetHeight;

    this.cellHeight.set(containerHeight / this.rows());
  };
  observer = new ResizeObserver(this.updateCellSize);
  constructor(
    private readonly apollo: Apollo,
    private readonly translate: TranslateService,
    private readonly toast: ToastService,
    private readonly mapService: MapService,
    private readonly permissionsService: PermissionsService,
    private readonly userService: UserSettingsService,
    private readonly cognitoService: CognitoService
  ) {
    this.uiSettings = this.userService.uiSettings;
    listenForEventsWhen(
      () => this.draggingItem() != null,
      document.body,
      'pointerup',
      (e) => this.onDrop(e)
    );
    listenForEventsWhen(
      () => this.draggingItem() != null,
      window,
      'pointerup',
      () => this.onMouseUp()
    );
    effect(() => {
      const el = this.container()?.nativeElement;
      if (!el) return;

      const handler = () => {
        this.isScrolled.set(el.scrollTop > 0);
      };

      el.addEventListener('scroll', handler);

      return () => {
        el.removeEventListener('scroll', handler);
      };
    });
    effect((onCleanUp) => {
      const el = this.container()?.nativeElement;
      if (el) {
        requestAnimationFrame(() => this.updateCellSize());
        this.observer.observe(el);
        onCleanUp(() => {
          this.observer.unobserve(el);
        });
      }
    });
    effect(
      () => {
        this.tmpWidgets.set(this.dashboard().widgets);
      },
      { allowSignalWrites: true }
    );
    effect(
      () => {
        this.refreshRate.set(this.dashboard().refreshRate);
      },
      { allowSignalWrites: true }
    );


    effect(() => {
      const widgets = this.tmpWidgets();
      const currentWidgetCount = widgets.length;
      const dashboardId = this.dashboardId();

      // Skip auto-repositioning if we're in the middle of a position update
      if (this.isUpdatingPosition) {
        return;
      }

      requestAnimationFrame(() => this.updateCellSize());

      // Check if we changed dashboard
      const dashboardChanged = this.currentDashboardId !== dashboardId;

      if (dashboardChanged) {
        console.log('Dashboard changed from', this.currentDashboardId, 'to', dashboardId);
        // Reset tracking when dashboard changes
        this.currentDashboardId = dashboardId;
        this.previousWidgetCount = currentWidgetCount;
        this.previousWidgetIds = new Set(widgets.map(w => w.id));
        // Do NOT auto-reposition on dashboard change
        return;
      }

      // Find newly added widgets (widgets that weren't in the previous set)
      const currentWidgetIds = new Set(widgets.map(w => w.id));
      const newlyAddedWidgets = widgets.filter(
        w => !this.previousWidgetIds.has(w.id)
      );

      console.log('Widget tracking:', {
        currentWidgetCount,
        previousWidgetCount: this.previousWidgetCount,
        newlyAddedWidgets: newlyAddedWidgets.map(w => ({ id: w.id, row: w.row, col: w.col })),
        previousWidgetIds: Array.from(this.previousWidgetIds),
        currentWidgetIds: Array.from(currentWidgetIds)
      });

      // Only reposition if there are newly added widgets (not on initial load)
      if (newlyAddedWidgets.length > 0 && this.previousWidgetCount > 0) {
        // Sort by row and col to find the newest (typically added at bottom-right)
        const sortedNewWidgets = [...newlyAddedWidgets].sort((a, b) => {
          if (a.row !== b.row) return b.row - a.row;
          return b.col - a.col;
        });

        const newestWidget = sortedNewWidgets[0];

        console.warn('AUTO-REPOSITIONING WIDGET:', {
          widgetId: newestWidget.id,
          currentPosition: { row: newestWidget.row, col: newestWidget.col }
        });

        // Reposition the newly added widget to the first available position
        setTimeout(() => {
          this.repositionToFirstAvailable(newestWidget.id);
        }, 100);
      }

      // Update tracking variables
      this.previousWidgetCount = currentWidgetCount;
      this.previousWidgetIds = currentWidgetIds;
    });
  }

  updateRefreshRate() {
    const refreshRate = this.refreshRate();
    if (!refreshRate) {
      return;
    }
    this.loading.set(true);
    this.apollo
      .mutate<
        UpdateDashboardRefreshRateMutation,
        UpdateDashboardRefreshRateMutationVariables
      >({
        mutation: updateDashboardRefreshRateMutation,
        variables: {
          dashboardId: this.dashboardId(),
          refreshRate,
        },
      })
      .subscribe({
        next: () => {
          this.onEndUpdateWidget.emit(this.dashboard());
          this.toast.notifySuccess(
            t(
              this.prefix,
              `Refresh rate was updated successfully`,
              this.translate
            )
          );
          this.apollo.client.refetchQueries({
            include: ['GetDashboards'],
          });
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }
  ngOnDestroy(): void {
    this.observer.disconnect();
  }
  setDraggingItem(itemId: string | null) {
    this.draggingItem.set(itemId);
  }

  onDrop(event: PointerEvent) {
    const currentlyDragged = this.draggingItem();
    if (!currentlyDragged) {
      return;
    }

    event.stopPropagation();

    const widget = this.tmpWidgets().find((w) => w.id === currentlyDragged);
    const el = this.widgetRefs()
      .find((w) => w.id() === currentlyDragged)
      ?.ref();
    if (!el || !widget) {
      this.draggingItem.set(null);
      return;
    }
    const rect = el.nativeElement.getBoundingClientRect();
    const containerRect =
      this.container()?.nativeElement.getBoundingClientRect();

    if (!containerRect) {
      this.draggingItem.set(null);
      return;
    }

    const cell = {
      row: Math.max(
        1,
        Math.min(
          Math.ceil((rect.top - (containerRect?.top ?? 0)) / this.cellHeight()),
          this.rows() - (widget?.rowSpan ?? 0) + 1
        )
      ),
      col: Math.max(
        1,
        Math.min(
          Math.ceil(
            (rect.left - (containerRect?.left ?? 0)) / this.cellWidth()
          ),
          COLS - (widget?.colSpan ?? 0) + 1
        )
      ),
    };

    // Only update if position actually changed
    if (cell.row !== widget.row || cell.col !== widget.col) {
      this.checkOverlapsAndUpdate(
        currentlyDragged,
        {
          col: cell.col,
          colSpan: widget.colSpan,
          row: cell.row,
          rowSpan: widget.rowSpan,
        },
        'drag'
      );
    }

    this.widgetRefs()
      .find((w) => w.id() === currentlyDragged)
      ?.resetPosition();
    this.draggingItem.set(null);
  }

  onMouseUp() {
    const currentlyDragged = this.draggingItem();
    if (currentlyDragged) {
      this.draggingItem.set(null);
      this.widgetRefs()
        .find((w) => w.id() === currentlyDragged)
        ?.resetPosition();
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.updateCellSize();
  }

  resizing(
    widgetId: string,
    e: { row: number; col: number; rowSpan: number; colSpan: number }
  ) {
    this.checkOverlapsAndUpdate(widgetId, e, 'resize');
  }

  private checkOverlapsAndUpdate(
    widgetId: string,
    position: { row: number; rowSpan: number; col: number; colSpan: number },
    origin: 'resize' | 'drag'
  ) {
    const currentWidget = this.tmpWidgets().find((w) => w.id === widgetId);

    // If widget doesn't exist in current dashboard, abort
    if (!currentWidget) {
      console.warn('Widget not found in current dashboard:', widgetId);
      return;
    }

    const currentPos = currentWidget;

    // Check if position is actually different before doing anything
    if (
      currentPos.row === position.row &&
      currentPos.col === position.col &&
      currentPos.rowSpan === position.rowSpan &&
      currentPos.colSpan === position.colSpan
    ) {
      // Position hasn't changed, no need to update
      return;
    }

    const overlaps = this.tmpWidgets().filter(
      (w) =>
        w.id !== widgetId &&
        ((w.row <= position.row && w.row + w.rowSpan >= position.row) ||
          (w.row >= position.row &&
            w.row <= position.row + position.rowSpan)) &&
        ((w.col <= position.col && w.col + w.colSpan >= position.col) ||
          (w.col >= position.col && w.col <= position.col + position.colSpan))
    );
    if (overlaps.length > 0 && origin === 'drag') {
      // drag mode won't try to adjust the overlap because it would be very complicated to try to predict what the user's intention were
      return;
    }
    const newPos = { ...position };
    if (overlaps.length) {
      const originalDelta = {
        row: newPos.row - currentPos.row,
        rowSpan: newPos.rowSpan - currentPos.rowSpan,
        col: newPos.col - currentPos.col,
        colSpan: newPos.colSpan - currentPos.colSpan,
      };
      const remainingDelta = {
        ...originalDelta,
      };
      /**
       * If row size has changed more than col size, we assume the main directional move is vertical, so we'll try to adjust the columns
       * before adjusting the rows. This is needed because if the widget overlaps with two or more widgets, the single overlap may be greater
       * on the other axis
       */
      const yMovement = originalDelta.rowSpan > originalDelta.colSpan;
      let cancel = false;
      const adjust = (area: {
        rowSpan: number;
        colSpan: number;
        row: number;
        col: number;
      }) => {
        // adjusting columns if both overlapping area size and yMovement are in agreement
        if (area.colSpan < area.rowSpan && yMovement) {
          if (area.col > newPos.col) {
            newPos.colSpan -= area.colSpan;
          } else {
            newPos.colSpan -= area.colSpan;
            newPos.col += area.colSpan;
          }
        } else if (!(area.colSpan < area.rowSpan) && !yMovement) {
          // adjusting rows if both overlapping area size and yMovement are in agreement
          if (area.row > newPos.row) {
            newPos.rowSpan -= area.rowSpan;
          } else {
            newPos.rowSpan -= area.rowSpan;
            newPos.row += area.rowSpan;
          }
        } else if (area.colSpan <= originalDelta.colSpan) {
          // adjusting columns if colSpan change is smaller than delta
          if (area.col > newPos.col) {
            newPos.colSpan -= area.colSpan;
          } else {
            newPos.colSpan -= area.colSpan;
            newPos.col += area.colSpan;
          }
        } else if (area.rowSpan <= originalDelta.rowSpan) {
          if (area.row > newPos.row) {
            // adjusting rows if rowSpan change is smaller than delta
            newPos.rowSpan -= area.rowSpan;
          } else {
            newPos.rowSpan -= area.rowSpan;
            newPos.row += area.rowSpan;
          }
        } else {
          // the best position is current position
          cancel = true;
        }
        if (
          newPos.rowSpan < 1 ||
          newPos.row < 1 ||
          newPos.col < 1 ||
          newPos.colSpan < 1 ||
          newPos.col + newPos.colSpan - 1 > COLS
        ) {
          cancel = true;
        }
      };
      overlaps.forEach((overlap) => {
        const overlapIsAbove =
          overlap.row <= newPos.row &&
          overlap.row + overlap.rowSpan >= newPos.row;
        if (overlapIsAbove) {
          const overlapIsRight =
            overlap.col >= newPos.col &&
            overlap.col <= newPos.col + newPos.colSpan;
          if (overlapIsRight) {
            adjust({
              row: newPos.row,
              rowSpan: overlap.row + overlap.rowSpan - newPos.row + 1,
              col: overlap.col,
              colSpan: newPos.col + newPos.colSpan - overlap.col + 1,
            });
          }
          const overlapIsLeft =
            overlap.col <= newPos.col &&
            overlap.col + overlap.colSpan >= newPos.col;
          if (overlapIsLeft) {
            adjust({
              row: newPos.row,
              rowSpan: overlap.row + overlap.rowSpan - newPos.row + 1,
              col: newPos.col,
              colSpan: overlap.col + overlap.colSpan - newPos.col + 1,
            });
          }
        }
        const overlapIsBelow =
          overlap.row >= newPos.row &&
          overlap.row <= newPos.row + newPos.rowSpan;
        if (overlapIsBelow) {
          const overlapIsRight =
            overlap.col >= newPos.col &&
            overlap.col <= newPos.col + newPos.colSpan;
          if (overlapIsRight) {
            adjust({
              row: overlap.row,
              rowSpan: newPos.row + newPos.rowSpan - overlap.row + 1,
              col: overlap.col,
              colSpan: newPos.col + newPos.colSpan - overlap.col + 1,
            });
          }
          const overlapIsLeft =
            overlap.col <= newPos.col &&
            overlap.col + overlap.colSpan >= newPos.col;
          if (overlapIsLeft) {
            adjust({
              row: overlap.row,
              rowSpan: newPos.row + newPos.rowSpan - overlap.row + 1,
              col: newPos.col,
              colSpan: overlap.col + overlap.colSpan - newPos.col + 1,
            });
          }
        }
      });
      if (cancel) {
        return;
      }
    }
    const delta = {
      row: newPos.row - currentPos.row,
      rowSpan: newPos.rowSpan - currentPos.rowSpan,
      col: newPos.col - currentPos.col,
      colSpan: newPos.colSpan - currentPos.colSpan,
    };
    if (delta.row || delta.col || delta.rowSpan || delta.colSpan) {
      console.log('Updating widget position:', {
        widgetId,
        from: currentPos,
        to: newPos,
        delta,
        origin
      });

      this.isUpdatingPosition = true;
      this.tmpWidgets.update((prev) =>
        prev.map((w) =>
          w.id === widgetId
            ? {
                ...w,
                ...newPos,
              }
            : w
        )
      );
      this.loading.set(true);
      this.apollo
        .mutate<
          UpdateWidgetsPositionMutation,
          UpdateWidgetsPositionMutationVariables
        >({
          mutation: updateWidgetsPositionMutation,
          variables: {
            dashboardId: this.dashboardId(),
            widgetPositions: [{ id: widgetId, ...newPos }],
          },
        })
        .subscribe({
          next: () => {
            this.apollo.client.refetchQueries({
              include: ['GetDashboards'],
            }).then(() => {
              this.isUpdatingPosition = false;
              this.loading.set(false);
              this.onEndUpdateWidget.emit(this.dashboard());
            });
          },
          error: () => {
            this.isUpdatingPosition = false;
            this.loading.set(false);
          },
        });
    }
  }

  renderingMaps = signal(true);
  async ngOnInit(): Promise<void> {
    await this.mapService.waitForAllMapsToRender();
    setTimeout(() => {
      this.renderingMaps.set(false);
    }, 3000);
  }

  deleteWidget(widgetId: string) {
    this.tmpWidgets.update((widgets) =>
      widgets.filter((w) => w.id !== widgetId)
    );
  }

  repositionWidget(widgetId: string, newPosition: { row: number; col: number; rowSpan: number; colSpan: number }) {
    console.log('repositionWidget called:', {
      widgetId,
      newPosition,
      stackTrace: new Error().stack
    });

    this.isUpdatingPosition = true;
    this.tmpWidgets.update((widgets) =>
      widgets.map((w) =>
        w.id === widgetId
          ? { ...w, ...newPosition }
          : w
      )
    );


    this.loading.set(true);
    this.apollo
      .mutate<
        UpdateWidgetsPositionMutation,
        UpdateWidgetsPositionMutationVariables
      >({
        mutation: updateWidgetsPositionMutation,
        variables: {
          dashboardId: this.dashboardId(),
          widgetPositions: [{ id: widgetId, ...newPosition }],
        },
      })
      .subscribe({
        next: () => {
          this.apollo.client.refetchQueries({
            include: ['GetDashboards'],
          }).then(() => {
            this.isUpdatingPosition = false;
            this.loading.set(false);
            this.onEndUpdateWidget.emit(this.dashboard());
          });
        },
        error: () => {
          this.isUpdatingPosition = false;
          this.loading.set(false);
        },
      });
  }


  private findFirstAvailablePosition(widgetWidth: number, widgetHeight: number, excludeWidgetId?: string): { row: number; col: number } {
    const widgets = this.tmpWidgets().filter(w => w.id !== excludeWidgetId);


    const occupiedCells = new Set<string>();
    widgets.forEach(widget => {
      for (let r = widget.row; r < widget.row + widget.rowSpan; r++) {
        for (let c = widget.col; c < widget.col + widget.colSpan; c++) {
          occupiedCells.add(`${r}-${c}`);
        }
      }
    });

    for (let row = 1; row <= this.visibleRows() + 10; row++) {
      for (let col = 1; col <= COLS - widgetWidth + 1; col++) {
        let canFit = true;


        for (let r = row; r < row + widgetHeight && canFit; r++) {
          for (let c = col; c < col + widgetWidth && canFit; c++) {
            if (occupiedCells.has(`${r}-${c}`)) {
              canFit = false;
            }
          }
        }

        if (canFit) {
          return { row, col };
        }
      }
    }


    return { row: this.visibleRows() + 1, col: 1 };
  }


  private repositionToFirstAvailable(widgetId: string) {
    const widget = this.tmpWidgets().find(w => w.id === widgetId);
    if (!widget) return;

    const newPosition = this.findFirstAvailablePosition(widget.colSpan, widget.rowSpan, widgetId);


    if (newPosition.row !== widget.row || newPosition.col !== widget.col) {
      this.repositionWidget(widgetId, {
        row: newPosition.row,
        col: newPosition.col,
        rowSpan: widget.rowSpan,
        colSpan: widget.colSpan
      });
    }
  }

  exporting = signal(false);
  async onExport() {
    this.loading.set(true);
    this.exporting.set(true);
    try {
      await this.createPDF();
      this.exporting.set(false);
      this.loading.set(false);
      this.toast.notifySuccess(
        t(this.prefix, `File generated successfully`, this.translate)
      );
    } catch (err) {
      this.exporting.set(false);
      this.loading.set(false);
      console.error(err);
      this.toast.notifyError(
        t(
          this.prefix,
          `An error occurred while generating export file`,
          this.translate
        )
      );
    }
  }

  private async createPDF() {
    const doc = new jsPDF();
    const pdfMarginTop = 20;
    const pdfMarginLeft = 20;
    const pdfMarginRight = 20;
    const paragraphSpacing = 8;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPosition = pdfMarginTop;

    let logoPath =
      this.uiSettings()?.logo?.custom?.toString() ??
      '/assets/images/ABB_logo.png';
    const img = new Image();
    img.src = logoPath;

    await new Promise((resolve) => (img.onload = resolve));

    const maxLogoWidth = 20;
    const maxLogoHeight = 10;
    const aspectRatio = img.width / img.height;
    const logoWidth =
      aspectRatio > maxLogoWidth / maxLogoHeight
        ? maxLogoWidth
        : aspectRatio * maxLogoHeight;
    const logoHeight =
      aspectRatio > maxLogoWidth / maxLogoHeight
        ? (1 / aspectRatio) * maxLogoWidth
        : maxLogoHeight;

    // Calcola la posizione x per allineare a destra
    const xPosition = pageWidth - logoWidth - pdfMarginRight;

    doc.addImage(logoPath, 'PNG', xPosition, yPosition, logoWidth, logoHeight);

    // Sposta la y position sotto il logo
    yPosition += logoHeight + paragraphSpacing;

    // Sezione titolo e data allineati orizzontalmente
    const currentDate = new Date().toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    // Carattere "—" sopra CUSTOM REPORT (text-abb-primary)
    doc.setFontSize(16);
    doc.setTextColor(255, 0, 15);
    doc.text('—', pdfMarginLeft, yPosition);

    yPosition += 6;

    // "DASHBOARD REPORT" sotto il carattere
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text('DASHBOARD REPORT', pdfMarginLeft, yPosition);

    yPosition += 5;

    // Titolo principale
    doc.setFontSize(16);
    doc.setTextColor(60);
    doc.text(this.dashboard().name, pdfMarginLeft, yPosition);

    // Data a destra (sulla stessa altezza del titolo principale)
    doc.setFontSize(10);
    doc.setTextColor(100); // Grigio per la data
    const dateText = `Report Generated on: ${currentDate}`;
    const dateWidth = doc.getTextWidth(dateText);
    const dateXPosition = pageWidth - dateWidth - pdfMarginRight;
    doc.text(dateText, dateXPosition, yPosition);

    yPosition += paragraphSpacing * 2; // Spazio extra prima del contenuto

    const dashboard = document.getElementById('dashboard-content');
    if (dashboard) {
      const canvas = await html2canvas(dashboard, {
        scale: 1.5,
        useCORS: true,
        allowTaint: true,
        logging: false,
        windowWidth: dashboard.scrollWidth,
        windowHeight: dashboard.scrollHeight,
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.8);

      const availableWidth = pageWidth - pdfMarginLeft * 2;
      const availableHeight = pageHeight - yPosition - pdfMarginTop;

      const imgAspectRatio = canvas.width / canvas.height;
      let imageWidth = availableWidth;
      let imageHeight = imageWidth / imgAspectRatio;

      if (imageHeight > availableHeight) {
        imageHeight = availableHeight;
        imageWidth = imageHeight * imgAspectRatio;
      }

      const imgXPosition = pdfMarginLeft + (availableWidth - imageWidth) / 2;

      doc.addImage(
        imgData,
        'JPEG',
        imgXPosition,
        yPosition,
        imageWidth,
        imageHeight
      );
      doc.save(`${this.dashboard().name}_Dashboard.pdf`);

      const pdfBase64 = doc.output('datauristring');
      const name = `${this.dashboard().name}_Dashboard`;
      const createdAt = new Date().toISOString();
      const userId = this.cognitoService.loggedInUser()?.id;

      await firstValueFrom(
        this.apollo.mutate({
          mutation: savePdfReport,
          variables: {
            name,
            createdAt,
            userId: userId?.substring(2),
            content: pdfBase64,
          },
        })
      );
    }
  }
}
